<template>
  <div v-if="!loading">
  <section v-if="comOrdersIsAny"
           class="cart">
    <div class="cart-header">
      <div class="cart-header-content">
        <h3 v-if="!isMultipleComputedOrders">
          {{ $t('pages.cart.yourOrder') }}
        </h3>
        <h3 v-else>
          {{ $t('pages.cart.chooseYourOrder') }}
        </h3>
        <span v-if="isModeHotel"
              class="cart-header-badge">
          {{ $t('pages.cart.step') }} 1
        </span>
        <span v-else
              class="cart-header-badge"
              :class="{ hidden: computedTableNum === null }">
          {{ `${$t('pages.cart.table')}: ${computedTableNum}` }}
        </span>
      </div>
      <button v-if="!isModeHotel"
              type="button"
              class="cart-header-history"
              @click="goToHistory">
        {{ $t('pages.cart.showHistoryShort') }}
      </button>
    </div>

    <div class="cart_list"
         :class="{ 'multiple-orders': isMultipleComputedOrders, 'single-order': !isMultipleComputedOrders }"
         v-for="order in computedOrders"
         :key="order.id">
      <ul v-if="isMultipleComputedOrders">
        <li>
          <ul>
            <li v-for="orderItem in order.items"
                :key="orderItem.id"
                :class="{ hidden: getItemPrice(orderItem) <= 0 }">
              <p>
                {{ orderItem.product.name }}
                <small>{{ `x ${getItemQuantity(orderItem)}` }}</small>
              </p>
              <em>
                {{ formatPrice(getItemPrice(orderItem)) }}
              </em>
            </li>
          </ul>
        </li>
      </ul>
      <ul v-else
          class="catalog_list">
        <li v-for="orderItem in order.items"
            :key="orderItem.uid || orderItem.id">
          <CartItem :item="orderItem" />
        </li>
      </ul>
      <div class="cart_list-total"
           v-if="isMultipleComputedOrders">
        <VButton variant="primary"
                 gradient
                 size="md"
                 fullWidth
                 v-on:click.native="openSingleOrder(order.id)">
          {{ $t('pages.cart.action') }} {{ formatPrice(getOrderSum(order)) }}
        </VButton>
      </div>
      <div class="cart-delivery-wrap"
           v-if="isModeHotel">
        <div class="cart-delivery-method">
          <h6>
            {{ $t('pages.cart.selectDeliveryMethod') }}:
          </h6>
          <RadioButton v-for="del in comDeliveryMethods"
                       :key="del.id"
                       :inputValue="del.id"
                       :label="del.name"
                       v-model="deliveryMethodId" />
        </div>
        <div class="cart-delivery-fee"
             v-if="!isSelfPickup">
          <span>{{ $t('pages.cart.deliveryFee') }}</span>
          <span class="price">{{ formatPrice(deliveryMethod.price) }}</span>
        </div>
      </div>
    </div>

    <div class="cart-totals"
         v-if="!isMultipleComputedOrders">
      <div class="cart-totals-row cart-totals-row--grand">
        <span class="cart-totals-label">{{ $t('pages.cart.total') }}</span>
        <span class="cart-totals-value">{{ comFormTotalPrice }}</span>
      </div>
    </div>

    <div class="vat-rules-block"
         v-if="!isMultipleComputedOrders && !isModeHotel">
      <div @click="showError = false">
        <CheckboxButton :label="$t('pages.cart.vatRulesTitle')"
                        v-model="vatRulesAccepted" />
      </div>
      <p class="vat-rules-note"
         v-if="vatRulesAccepted">{{ $t('pages.cart.vatRulesDescription') }}</p>
    </div>

    <div class="cart_options">
      <div class="vat-rules-block"
           v-if="!isMultipleComputedOrders">
        <div v-if="computedCompanyData.locale.smart_menu_sms_enabled">
          <VuePhoneNumberInput v-model="clientPhone"
                               default-country-code="PL"
                               @update="updatePhone"
                               :no-example="true"
                               :preferred-countries="['PL', 'DE', 'ES', 'FR', 'UA', 'US']"
                               :translations="getPhoneInputTranslations()"
                               :border-radius="12"
                               required />
          <div class="phone_info">
            <svg width="16"
                 height="16"
                 viewBox="0 0 16 16"
                 fill="none"
                 xmlns="http://www.w3.org/2000/svg">
              <path d="M8 1.5C6.71443 1.5 5.45772 1.88122 4.3888 2.59545C3.31988 3.30968 2.48676 4.32484 1.99479 5.51256C1.50282 6.70028 1.37409 8.00721 1.6249 9.26809C1.8757 10.529 2.49477 11.6872 3.40381 12.5962C4.31285 13.5052 5.47104 14.1243 6.73192 14.3751C7.99279 14.6259 9.29973 14.4972 10.4874 14.0052C11.6752 13.5132 12.6903 12.6801 13.4046 11.6112C14.1188 10.5423 14.5 9.28558 14.5 8C14.4982 6.27665 13.8128 4.62441 12.5942 3.40582C11.3756 2.18722 9.72335 1.50182 8 1.5ZM8 13.5C6.91221 13.5 5.84884 13.1774 4.94437 12.5731C4.0399 11.9687 3.33495 11.1098 2.91867 10.1048C2.50238 9.09977 2.39347 7.9939 2.60568 6.927C2.8179 5.86011 3.34173 4.8801 4.11092 4.11091C4.8801 3.34172 5.86011 2.8179 6.92701 2.60568C7.9939 2.39346 9.09977 2.50238 10.1048 2.91866C11.1098 3.33494 11.9687 4.03989 12.5731 4.94436C13.1774 5.84883 13.5 6.9122 13.5 8C13.4984 9.45818 12.9184 10.8562 11.8873 11.8873C10.8562 12.9184 9.45819 13.4983 8 13.5ZM9 11C9 11.1326 8.94733 11.2598 8.85356 11.3536C8.75979 11.4473 8.63261 11.5 8.5 11.5C8.23479 11.5 7.98043 11.3946 7.7929 11.2071C7.60536 11.0196 7.5 10.7652 7.5 10.5V8C7.3674 8 7.24022 7.94732 7.14645 7.85355C7.05268 7.75979 7 7.63261 7 7.5C7 7.36739 7.05268 7.24022 7.14645 7.14645C7.24022 7.05268 7.3674 7 7.5 7C7.76522 7 8.01957 7.10536 8.20711 7.29289C8.39465 7.48043 8.5 7.73478 8.5 8V10.5C8.63261 10.5 8.75979 10.5527 8.85356 10.6464C8.94733 10.7402 9 10.8674 9 11ZM7 5.25C7 5.10166 7.04399 4.95666 7.1264 4.83332C7.20881 4.70999 7.32595 4.61386 7.46299 4.55709C7.60004 4.50032 7.75084 4.48547 7.89632 4.51441C8.04181 4.54335 8.17544 4.61478 8.28033 4.71967C8.38522 4.82456 8.45665 4.9582 8.48559 5.10368C8.51453 5.24917 8.49968 5.39997 8.44291 5.53701C8.38615 5.67406 8.29002 5.79119 8.16668 5.8736C8.04334 5.95601 7.89834 6 7.75 6C7.55109 6 7.36033 5.92098 7.21967 5.78033C7.07902 5.63968 7 5.44891 7 5.25Z"
                    fill="currentColor" />
            </svg>
            {{ $t('pages.cart.phoneInfo') }}
          </div>

        </div>

        <div class="adult-agreement"
             v-if="!isMultipleComputedOrders && cartHasAdultProducts"
             @click="showError = false">
          <CheckboxButton :label="$t('pages.cart.adultCheckboxTitle')"
                          v-model="adultAgreement" />
        </div>

        <p class="vat-rules-note"
           v-if="!adultAgreement && cartHasAdultProducts && showError">{{ $t('pages.cart.adultCheckboxError') }}</p>
      </div>
      <div v-if="!isModeHotel"
           class="history-and-terms">
        <div class="cart_options default_button regulamin_text"
             v-if="showSmRegulamin">{{ $t('pages.cart.regulaminCheckboxTitle1') }} <a @click="showRegulamin"
             href="#"
             id="regulamin-link">{{ $t('pages.cart.regulaminCheckboxTitle2') }}</a></div>
      </div>

    </div>
  </section>
  <section v-else
           class="cart cart--empty">
    <div class="cart-header">
      <div class="cart-header-content">
        <h3>
          {{ $t('pages.cart.yourOrder') }}
        </h3>
      </div>
    </div>
    <div class="cart-empty">
      <span class="cart-empty__icon">
        <svg width="32" height="32" viewBox="0 0 18 20" fill="none">
          <path d="M11.1597 2.3788C11.179 2.35447 11.2031 2.33433 11.2305 2.31961C11.2578 2.30488 11.2879 2.29587 11.3188 2.29312C11.3498 2.29038 11.381 2.29396 11.4105 2.30365C11.44 2.31333 11.4673 2.32892 11.4906 2.34947L12.3358 3.0938L9.67743 6.41672H11.4374L13.3679 4.00405L14.8823 5.33963C14.927 5.3792 14.9546 5.4347 14.959 5.49429C14.9635 5.55388 14.9445 5.61285 14.9061 5.65863L14.2736 6.41672H16.0556C16.2763 6.09357 16.3713 5.70088 16.3227 5.31257C16.2741 4.92427 16.0852 4.56712 15.7916 4.30838L12.3999 1.3173C12.2363 1.1732 12.045 1.06397 11.8378 0.996276C11.6305 0.928581 11.4117 0.903844 11.1945 0.923575C10.9774 0.943307 10.7666 1.00709 10.5749 1.11104C10.3833 1.21498 10.2148 1.3569 10.0798 1.52813L6.22802 6.41672H7.97885L11.1597 2.3788ZM2.81252 5.95838C2.63018 5.95838 2.45531 6.03082 2.32638 6.15975C2.19745 6.28868 2.12502 6.46355 2.12502 6.64588C2.12502 6.82822 2.19745 7.00309 2.32638 7.13202C2.45531 7.26095 2.63018 7.33338 2.81252 7.33338H14.7292C15.5193 7.33338 16.2771 7.64726 16.8358 8.20596C17.3945 8.76466 17.7083 9.52243 17.7083 10.3125V16.2709C17.7083 17.061 17.3945 17.8188 16.8358 18.3775C16.2771 18.9362 15.5193 19.25 14.7292 19.25H3.72918C2.93906 19.25 2.1813 18.9362 1.62259 18.3775C1.06389 17.8188 0.750015 17.061 0.750015 16.2709V6.64588C0.750015 6.09887 0.967314 5.57427 1.35411 5.18748C1.7409 4.80068 2.26551 4.58338 2.81252 4.58338H6.77252L5.67985 5.95838H2.81252ZM12.2083 13.5209C12.2083 13.9004 12.5163 14.2084 12.8958 14.2084H14.7292C14.9115 14.2084 15.0864 14.136 15.2153 14.007C15.3442 13.8781 15.4167 13.7032 15.4167 13.5209C15.4167 13.3385 15.3442 13.1637 15.2153 13.0347C15.0864 12.9058 14.9115 12.8334 14.7292 12.8334H12.8958C12.7135 12.8334 12.5386 12.9058 12.4097 13.0347C12.2808 13.1637 12.2083 13.3385 12.2083 13.5209Z" fill="currentColor"/>
        </svg>
      </span>
      <div class="cart-empty__title">{{ $t('pages.cart.emptyCartTitle') }}</div>
      <div class="cart-empty__sub">{{ $t('pages.cart.emptyCartMessage') }}</div>
      <router-link :to="{ name: 'menu' }"
                   class="cart-empty__cta">
        {{ $t('pages.cart.returnToMenu') }}
      </router-link>
      <a v-if="!isModeHotel"
         class="cart-empty__history"
         @click="goToHistory">{{ $t('pages.cart.showHistory') }}</a>
    </div>
  </section>
</div>
</template>

<script>
import VButton from "@/components/ui/VButton.vue";
import loader from "@/plugins/loader.plugin";
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import CheckboxButton from "@/components/CheckboxButton.vue";
import RadioButton from "@/components/RadioButton.vue";
import { getItemQuantity, getItemPrice } from "@/utils/itemHelper";
import CartItem from "./CartItem.vue";
import { EventBus } from "@/utils/eventBus";

export default {
  name: "CartTab",
  props: ['orderId'],
  components: {
    VButton,
    VuePhoneNumberInput,
    CheckboxButton,
    RadioButton,
    CartItem,
  },
  data() {
    return {
      isMultipleComputedOrders: false,
      computedOrders: [],
      loading: false,
      allOrders: [],
      ordersSum: 0,
      vatRulesAccepted: false,
      regulaminAccepted: false,
      buyButtonPressed: false,
      adultAgreement: false,
      showError: false,
      errorMessage: '',
      clientPhone: '',
      formattedPhone: '',
      sm_terms: this.$store.getters['getCompanyData'].locale.sm_terms,
      sm_show_terms: this.$store.getters['getCompanyData'].locale.sm_show_terms,
      activeIcon: null,
      isQsrMode: false,
      iconsOptions: {
        "vegeterian": {
          "name": "vegeterian",
          "description": "vegeterian-description",
          "icon": "iconmoon-vegeterian"
        },
        "gluten-free": {
          "name": "gluten-free",
          "description": "gluten-free-description",
          "icon": "iconmoon-gluten-free"
        },
        "chili-pepper": {
          "name": "chili-pepper",
          "description": "chili-pepper-description",
          "icon": "iconmoon-chili-pepper"
        },
        "low-calories": {
          "name": "low-calories",
          "description": "low-calories-description",
          "icon": "iconmoon-low-calories"
        },
      },
    };
  },
  directives: {
    mask: {
      inserted: function (el, binding) {

        var mask = binding.value,
          first = mask.indexOf('_'),
          // fieldsL = mask.replace(/[^_]/gm, '').length,
          clean = mask.replace(/[^0-9_]/gm, ''),
          indexes = [];

        for (var i = 0; i < clean.length; i++) {
          if (!isNaN(clean[i])) {
            indexes.push(i);
          }
        }

        el.value = mask;
        el.clean = mask.replace(/[^0-9]/gm, '');

        function maskIt() {
          var value = el.value,
            filtred = value.replace(/[^0-9]/gm, ''),
            result = '';

          if (value.length < first) {
            value = mask + value;
            filtred = value.replace(/[^0-9]/gm, '');
          }

          for (var i = 0; i < filtred.length; i++) {
            if (indexes.indexOf(i) == -1) {
              result += filtred[i];
            }
          }

          value = '';
          var cursor = 0;

          for (i = 0; i < mask.length; i++) {
            if (mask[i] == '_' && result) {
              value += result[0];
              result = result.slice(1);
              cursor = i + 1;

            } else {
              value += mask[i];
            }
          }

          if (cursor < first) {
            cursor = first;
          }

          el.value = value;

          el.clean = el.value.replace(/[^0-9]/gm, '');

          el.setSelectionRange(cursor, cursor);
        }

        el.addEventListener('focus', function (event) {
          event.preventDefault();
        });

        el.addEventListener('click', function (event) {
          event.preventDefault();
          var start = el.value.indexOf('_');

          if (start == -1) {
            start = el.value.length;
          }

          el.setSelectionRange(start, start);

        });

        el.addEventListener('paste', function () {
          var start = el.selectionStart;

          if (start < first) {
            el.value = '_' + el.value;
          }
        });

        el.addEventListener('input', function (event) {
          var start = el.selectionStart;
          maskIt(event, start);
        });

      }
    }
  },
  mounted() {
    this.showLoader();
    // this.isQsrMode = this.$store.getters['getIsModeQSR'];
    this.isQsrMode = this.$store.getters['getCartEnabled'];

    if (this.deliveryMethod === null) {
      this.deliveryMethod = this.comDeliveryMethods[0];
    }
    // this.$store.dispatch('cartStore/FETCH_AND_SET_ORDERS_BY_TABLE', this.$store).then(this.setComputedOrders)
    this.getComputedOrders();
    this.updateStoreOrderId();

    // this.clientPhone = window.localStorage.getItem('sm_clientPhone') || '';

    // The BottomDock "Pay" plate triggers this screen's own validated payment.
    EventBus.$on('sm:pay', this.handlePayment);
  },
  beforeDestroy() {
    EventBus.$off('sm:pay', this.handlePayment);
  },
  computed: {
    computedTableNum: function () {
      return this.$store.getters['getTableName'];
    },
    computedCompanyData: function () {
      return this.$store.getters['getCompanyData'];
    },
    computedOrdersAmount: function () {
      let orders = this.computedOrders,
        amount = 0;
      if (orders.length) {
        orders.forEach(order => {
          if (order.amount) {
            amount += order.amount;
          } else {
            order.items.forEach(item => {
              amount += item.amount;
              // amount += item.price * item.count
            });
          }
        });
      }
      return this.formatPrice(amount);
    },
    countCartItems() {
      let amount = 0,
        orders = this.computedOrders;

      if (orders.length) {
        orders.forEach(item => {
          amount += item.count;
        });
      }
      return amount;
    },
    cartHasAdultProducts() {
      return this.computedOrders.some(order => order.items.some(item => item.is_adult));
    },
    computedCurrentLocale: function () {
      let locale = null,
        prefLangCode = this.$store.state.prefLangCode ? this.$store.state.prefLangCode : 'en';
      this.$store.state.companyData.languages.forEach((lang) => {
        if (lang.code === prefLangCode) {
          locale = lang;
          return;
        }
      });
      return locale;
    },
    showSmRegulamin() {
      return (this.sm_show_terms && this.sm_terms && this.sm_terms.length > 0);
    },
    isModeHotel() {
      return this.$store.getters['getIsModeHOT'];
    },
    comDeliveryMethods() {
      return this.computedCompanyData.locale.sm_delivery_methods;
    },
    comFormTotalPrice() {
      if (this.$store.getters['getIsModeWMM']) {
        return this.ordersSum;
      }
      return this.$store.getters['cartStore/getFormattedCartTotal'];
    },
    deliveryMethod: {
      get() {
        return this.$store.getters['cartStore/getDeliveryMethod'];
      },
      set(val) {
        this.$store.dispatch('cartStore/SET_DELIVERY_METHOD', val);
      }
    },
    deliveryMethodId: {
      get() {
        return this.deliveryMethod.id;
      },
      set(val) {
        this.deliveryMethod = this.comDeliveryMethods.find(del => del.id === val);
      }
    },
    isSelfPickup() {
      return this.deliveryMethod && this.deliveryMethod.self_pickup === 1;
    },
    comOrdersIsAny() {
      if(this.computedOrders.length === 0) {
        return false;
      }
      return this.computedOrders.some(order => order.items.length > 0);
    }
  },
  methods: {
    formatPrice(price) {
      return new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(price);
    },
    showErrorMesage(message) {
      this.showError = true;
      this.errorMessage = message;
    },
    handlePayment() {
      if (this.cartHasAdultProducts && !this.adultAgreement) {
        this.showErrorMesage(this.$t('pages.cart.adultCheckboxError'));
        return;
      }

      if (this.isModeHotel) {
        this.$router.push({ name: 'payment' });
        return;
      }

      // if(!this.regulaminAccepted) {
      //   this.showErrorMesage(this.$t('pages.cart.regulaminCheckboxError'));
      //   return;
      // }

      // return true;
      if (this.computedCompanyData.locale.smart_menu_sms_enabled) {
        var phone = this.formattedPhone.e164;

        if (!this.formattedPhone.isValid) {
          this.$toast.error(this.$t('pages.cart.invalidPhone'), { position: 'top-center' });
          return;
        }
        window.localStorage.setItem('sm_clientPhone', phone);
        this.$store.dispatch('cartStore/SET_CLIENT_PHONE', phone);
      }

      // if(!this.vatRulesAccepted) {
      //   this.buyButtonPressed = true;
      //   console.log(2);
      //   return
      // }
      this.$store.dispatch('cartStore/MAKE_A_PAYMENT');
    },
    removeFromCart(item) {
      this.$store.dispatch('cartStore/REMOVE_ITEM_FROM_CART', item.id)
        .then(() => this.$toast.success(this.$t('removedFromCart'), { position: 'top-center' }));
    },
    goToHistory() {
      this.$router.push({ name: 'history' });
    },
    openSingleOrder(orderId) {
      this.$router.push('/bill/' + orderId);
    },
    getOrderSum(order) {
      var total = 0;

      order.items.forEach(item => {
        if (item.amount) {
          total = total + parseFloat(item.amount);
        } else {
          total = total + parseFloat(item.price) * parseFloat(item.count);
        }
        // total = total + parseFloat(item.amount);
      });
      return total;
    },
    getOrdersSum() {
      var total = 0;
      //if wmm mode
      if (this.$store.getters['getIsModeWMM']) {
        this.computedOrders.forEach(order => {
          total = total + parseFloat(order.amount);
          // order.items.forEach(item => {
          //   if(item.amount) {
          //     total = total + parseFloat(item.amount);
          //   } else {
          //     total = total + parseFloat(item.price) * parseFloat(item.count);
          //   }
          // });
        });
      } else {
        this.computedOrders.forEach(order => {
          order.items.forEach(item => {
            if (item.amount) {
              total = total + parseFloat(item.amount);
            } else {
              total = total + parseFloat(item.price) * parseFloat(item.count);
            }
          });
        });
      }

      if (this.isModeHotel && this.deliveryMethod) {
        total += this.deliveryMethod.price;
      }


      this.ordersSum = this.formatPrice(total);
    },
    setIsMultipleComputedOrders: function () {
      this.getOrdersSum();
      this.hideLoader();
      this.isMultipleComputedOrders = !this.orderId && this.computedOrders.length > 1;
      this.updateStoreOrderId();
    },
    getOrders() {
      var instance = this;
      // Single source of truth: reload through the SAME store action the bottom
      // dock uses. The action is single-flight (one request on the wire at a
      // time — parallel calls made the backend's POS sync race itself and return
      // duplicated orders) and does the PLACED/amount filtering + id-dedupe, so
      // the bill and the dock always render the same server state. Hand
      // setAllOrders shallow copies: it trims zero-quantity lines and must not
      // mutate the store's order objects the dock is reading.
      this.$store.dispatch('cartStore/FETCH_TABLE_ORDERS')
        .then(orders => {
          instance.setAllOrders(orders.map(order => ({
            ...order,
            items: (order.items || []).slice()
          })));
        })
        .catch(error => {
          instance.hideLoader();
          console.log(error);
        });
    },
    getComputedOrders: function () {
      if (this.$store.getters['getIsModeWMM']) {
        this.getOrders();
      } else {
        let orders = [];
        let items = this.$store.getters['cartStore/getCart'];

        if (items.length) {
          orders = [{
            id: 1,
            items: this.$store.getters['cartStore/getCart']
          }];
        }
        // this.$store.dispatch('cartStore/SET_ORDERS_BY_TABLE', orders)
        // console.log(orders)
        this.setAllOrders(orders);
      }
    },
    setComputedOrders: function () {
      if (this.orderId) {
        this.computedOrders = this.allOrders.filter(order => parseInt(order.id) === parseInt(this.orderId));
      } else {
        this.computedOrders = this.allOrders;
      }

      this.setIsMultipleComputedOrders();
    },
    getProductName: function (orderItem) {
      return orderItem.product.name;
    },
    setAllOrders: function (orders) {
      if (this.$store.getters['getIsModeWMM']) {
        orders.forEach(order => {
          var orderItems = [];
          order.items.forEach(item => {
            if (item.quantity > 0) {
              orderItems.push(item);
            }
          });
          order.items = orderItems;
        });
      }

      this.allOrders = orders;
      this.setComputedOrders(orders);
    },
    showRegulamin() {
      this.$emit('show-regulamin');
    },
    showLoader() {
      this.loading = true;
      loader.show();
    },
    hideLoader() {
      this.loading = false;
      loader.hide();
    },
    updateStoreOrderId() {
      var orderId = this.orderId;
      if (!orderId && this.computedOrders.length > 0) {
        orderId = this.computedOrders[0].id;
      }
      this.$store.dispatch('cartStore/SET_ORDER_ID', orderId);
    },
    unmaskedValue: function () {
      return this.$refs.input.clean;
    },
    isValidClientPhone() {
      var phone = this.unmaskedValue();
      return phone.length >= 11;
    },
    showIconDetails(icon) {
      this.activeIcon = this.iconsOptions[icon];
      this.$refs.iconDetails.open();
    },
    getItemCount: function () {
      return this.itemCount;
    },
    updateItemQuantity(orderItem, quantity) {
      this.$store.dispatch('cartStore/UPDATE_ITEM_QUANTITY', { id: orderItem.id, quantity });
      this.setComputedOrders();
    },
    updatePhone(value) {
      this.formattedPhone = value;
    },
    getPhoneInputTranslations() {
      return {
        countrySelectorLabel: this.$t('pages.cart.countrySelectorLabel'),
        countrySelectorError: this.$t('pages.cart.countrySelectorError'),
        phoneNumberLabel: this.$t('pages.cart.phoneNumberLabel')
      };
    },
    getOrderButtonText() {
      var text = this.$t('pages.cart.pay');
      if (this.isModeHotel) {
        text = this.$t('pages.cart.continue'); //TODO add de end es transaltions
      }
      else if (this.$store.getters['getIsModeQSR']) {
        text = this.$t('pages.cart.orderAndPay');
      }
      else if (this.$store.getters['getIsModeSOT']) {
        text = this.$t('pages.cart.order');
      }
      return text;
    },
    getItemQuantity(item) {
      return getItemQuantity(item);
    },
    getItemPrice(item, obj) {
      return getItemPrice(item, obj);
    }
  },
  watch: {
    $route(to) { //to, from
      this.showLoader();
      if (to.fullPath === '/bill') {
        this.getComputedOrders();
      } else {
        this.setComputedOrders();
      }

      // this.show = false;
    }
  }
};
</script>

<style lang="scss" scoped>
ul,
li {
  margin: 0;
  padding: 0;
  list-style: none;
}

h6 {
  margin: 0;
  padding: 0;

  font-size: var(--text-md);
  font-weight: var(--weight-bold);
  color: var(--text-primary);

  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: -webkit-box;
}

.cart {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* viewport units, not %: the document scrolls, ancestor heights are content-driven */
  min-height: 100vh;
  min-height: 100svh;
  /* room for the floating nav (incl. the gradient pay plate) so the last item
     and totals card clear it */
  padding-bottom: calc(var(--nav-height) + 76px);

  &--empty {
    padding-bottom: 24px;
  }
}

/* ---- Frosted header ---- */
/* Shares the VPageHeader treatment; kept inline here because this header carries
   a dynamic title + table badge + venue logo and lives inside VContainer (the
   negative margin pulls it edge-to-edge). */
.cart-header {
  position: sticky;
  top: 0;
  z-index: 1100;

  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;

  margin: 0 -16px;
  padding: calc(20px + var(--safe-top, 0px)) 20px 14px;

  background: var(--header-blur-bg);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  border-bottom: 1px solid var(--border-subtle);
  box-shadow: inset 0 1px 0 var(--header-hairline);

  &-content {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px;
    min-width: 0;

    &>h3 {
      margin: 0;
      padding: 0;

      font-size: 27px;
      font-weight: var(--weight-black);
      letter-spacing: -0.02em;
      line-height: 1.1;
      color: var(--text-primary);
    }
  }

  &-badge {
    display: inline-flex;
    align-items: center;

    padding: 4px 12px;
    border-radius: var(--radius-pill);

    font-size: var(--text-xs);
    font-weight: var(--weight-bold);

    background: var(--color-primary-soft);
    color: var(--color-primary);

    &.hidden {
      visibility: hidden;
    }
  }

  /* Trailing pill button — mirrors .favorites-clear in MenuFavorites.vue so the
     secondary header action reads the same across the app. */
  &-history {
    flex-shrink: 0;
    padding: 8px 14px;

    font-family: var(--font-sans);
    font-weight: var(--weight-bold);
    font-size: 13px;
    line-height: 1;
    white-space: nowrap;

    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-pill);
    background: var(--surface-card);
    color: var(--text-secondary);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    transition: transform var(--dur-fast) var(--ease-standard);

    &:active { transform: scale(0.94); }
  }
}


/* ---- Empty cart ---- */
/* Mirrors the favorites empty state (.favorites-empty in MenuFavorites.vue) —
   keep the two in sync so empty screens read as one pattern. */
.cart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;

  padding: 64px 24px;
}

.cart-empty__icon {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 72px;
  height: 72px;

  border-radius: 50%;

  background: var(--color-primary-soft);
  color: var(--color-primary);
}

.cart-empty__title {
  font-size: 18px;
  font-weight: var(--weight-bold);
  color: var(--text-primary);
}

.cart-empty__sub {
  max-width: 260px;

  font-size: 14px;
  line-height: 1.4;
  color: var(--text-secondary);
}

.cart-empty__cta {
  margin-top: 6px;
  padding: 11px 22px;

  font-weight: var(--weight-bold);
  font-size: 14px;
  line-height: 1;

  border-radius: var(--radius-pill);
  background: var(--grad-brand);
  box-shadow: var(--shadow-brand);
  color: var(--color-on-primary);
  text-decoration: none;
}

.cart-empty__history {
  font-size: var(--text-sm);
  font-weight: var(--weight-semibold);
  color: var(--text-secondary);
  text-decoration: none;
  cursor: pointer;
  transition: color var(--dur-fast) var(--ease-standard);

  &:active { color: var(--text-primary); }
}

/* ---- Multi-order summary list ---- */
.cart_list.multiple-orders {
  display: flex;
  flex-direction: column;
  border: 1px solid var(--border-subtle);
  padding: 16px;
  gap: 12px;
  border-radius: 20px;
  box-shadow: 0 5px 16px rgba(74, 55, 73, 0.05);
  background-color: var(--surface-card);

  &>ul {
    display: flex;
    flex-direction: column;
    gap: 30px;

    li {
      width: 100%;

      &>span {
        display: block;
        text-align: center;

        font-weight: var(--weight-bold);
        font-size: var(--text-md);
        color: var(--text-primary);
      }

      &>ul {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px;
        background-color: var(--surface-subtle);
        border-radius: var(--radius-2xl);

        li {
          display: flex;
          justify-content: space-between;
          align-items: center;

          p {
            color: var(--text-primary);
            font-size: var(--text-base);
            font-weight: var(--weight-medium);
            margin: 0;

            small {
              color: var(--color-primary);
              font-size: var(--text-base);
              font-weight: var(--weight-semibold);
            }

          }

          em {
            color: var(--text-primary);

            font-size: var(--text-base);
            font-style: normal;
            font-weight: var(--weight-bold);
          }
        }
      }
    }
  }

  .cart_list-total {
    display: flex;
    align-items: center;
    justify-content: space-between;
    color: var(--text-primary);

    span {
      font-weight: var(--weight-bold);
      font-size: var(--text-md);
    }
  }

  &.multiple-orders {
    .cart_list-total {
      padding-top: 10px;
      border-top: none;

      div {
        width: 100%;
      }
    }
  }

  &.single-order {
    box-shadow: none;
    border: none;
    padding: 0;
    background: transparent;
  }
}

/* ---- Totals card ---- */
.cart-totals {
  display: flex;
  flex-direction: column;
  gap: 12px;

  padding: 18px;

  background: var(--surface-card);
  border: 1px solid var(--border-subtle);
  border-radius: 20px;

  &-row {
    display: flex;
    align-items: baseline;
    justify-content: space-between;

    &--grand {
      .cart-totals-label {
        font-size: 18px;
        font-weight: var(--weight-black);
        color: var(--text-primary);
      }

      .cart-totals-value {
        font-size: 22px;
        font-weight: var(--weight-black);
        color: var(--text-price);
      }
    }
  }

  &-label {
    font-size: var(--text-base);
    color: var(--text-secondary);
  }

  &-value {
    font-size: var(--text-md);
    font-weight: var(--weight-bold);
    color: var(--text-primary);
  }
}

.cart_options {
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: auto;


  &.default_button {
    font-size: var(--text-sm);
    font-weight: var(--weight-semibold);
    color: var(--text-secondary);
    text-decoration: none;
    text-align: center;
    cursor: pointer;
    transition: color var(--dur-fast) var(--ease-standard);

    &:active { color: var(--text-primary); }
  }

  &.regulamin_text {
    display: block;

    color: var(--text-muted);
    font-weight: var(--weight-medium);
    font-size: var(--text-sm);
    line-height: 1.5;

    a {
      color: var(--color-primary);
      font-weight: var(--weight-semibold);
      text-decoration: underline;
      text-underline-offset: 2px;
    }
  }
}

.cart-delivery-wrap {
  margin-top: 6px;

  .cart-delivery-method {
    display: grid;
    gap: 14px;
  }

  .cart-delivery-fee {
    display: flex;
    justify-content: space-between;

    margin-top: 18px;

    font-weight: var(--weight-medium);
    font-size: var(--text-base);

    color: var(--text-secondary);

    .price {
      font-weight: var(--weight-bold);
      color: var(--text-primary);
    }
  }
}



.history-and-terms {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.vat-rules-block {
  display: flex;
  flex-direction: column;
  gap: 14px;

  label {
    font-size: var(--text-base);
  }

  .vat-rules-note {
    padding: 12px 14px;
    color: var(--danger);
    border: 1px solid var(--danger);
    background: var(--danger-soft);
    border-radius: var(--radius-xl);
    margin: 0;
    font-size: var(--text-sm);
  }

  .phone_info {
    display: flex;
    align-items: center;
    margin-top: 10px;
    padding-left: 3px;

    font-size: var(--text-sm);
    color: var(--text-secondary);

    svg {
      flex-shrink: 0;
      margin-right: 7px;
      color: var(--color-primary);
    }
  }

  /* token-skinned phone field */
  ::v-deep .vue-phone-number-input {
    .input-tel__input,
    .country-selector__input {
      border: 1px solid var(--border-subtle) !important;
      box-shadow: none !important;
    }

    .input-tel__input:focus,
    .country-selector__input:focus {
      box-shadow: none !important;
    }

    .input-tel__label,
    .country-selector__label {
      color: var(--text-muted) !important;
    }
  }
}

.adult-agreement {
  margin-bottom: 4px;

  label {
    padding-left: 0;
  }
}

.catalog_list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.hidden {
  display: none !important;
}

#regulamin-link {
  text-decoration: underline;
}

/* The shared radio/checkbox icons ship a hardcoded violet — recolor them. */

/* Radios = an active choice (e.g. delivery method) → follow the brand. */
::v-deep .radio-wrapper {
  .radio-label {
    color: var(--text-primary);
  }

  .icon-on rect,
  .icon-on path {
    stroke: var(--color-primary);
  }

  .icon-on rect:first-child {
    fill: var(--color-primary-soft);
  }

  .icon-on rect + rect,
  .icon-on path {
    fill: var(--color-primary);
  }
}

/* Checkboxes = utility/legal toggles (VAT invoice, adult agreement) → neutral,
   so they don't read as a promoted brand action. */
::v-deep .checkbox-wrapper {
  .checkbox-label {
    color: var(--text-primary);
  }

  .icon-on rect {
    fill: var(--surface-subtle);
    stroke: var(--border-strong);
  }

  .icon-on path {
    fill: var(--text-primary);
  }
}
</style>

<style>
.bottom-sheet__content {
  touch-action: unset !important;
  -webkit-user-select: unset !important;
  -webkit-touch-callout: unset !important;
  -webkit-user-drag: unset !important;
}
</style>
